import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr, type LedgerAccount } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

type LineDraft = {
  key: string;
  ledgerAccountId: string;
  debit: string;
  credit: string;
  narration: string;
};

function emptyLine(): LineDraft {
  return {
    key: Math.random().toString(36).slice(2),
    ledgerAccountId: "",
    debit: "",
    credit: "",
    narration: "",
  };
}

export default function AccountingVoucherFormPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [voucherDate, setVoucherDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(), emptyLine()]);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounting-accounts", hospitalId],
    queryFn: () => accountingApi.listLedgerAccounts(hospitalId),
    enabled: !!hospitalId,
  });

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      debit += Number(l.debit) || 0;
      credit += Number(l.credit) || 0;
    }
    return {
      debit: Math.round(debit * 100) / 100,
      credit: Math.round(credit * 100) / 100,
      balanced: Math.abs(debit - credit) < 0.005 && debit > 0,
    };
  }, [lines]);

  const createMut = useMutation({
    mutationFn: async (andPost: boolean) => {
      const payload = {
        voucherDate,
        voucherType: "JOURNAL" as const,
        narration: narration || undefined,
        lines: lines
          .filter((l) => l.ledgerAccountId && (Number(l.debit) || Number(l.credit)))
          .map((l) => ({
            ledgerAccountId: l.ledgerAccountId,
            debit: Number(l.debit) || 0,
            credit: Number(l.credit) || 0,
            narration: l.narration || undefined,
          })),
      };
      const draft = await accountingApi.createVoucher(payload, hospitalId);
      if (andPost) {
        return accountingApi.postVoucher(draft.id, hospitalId);
      }
      return draft;
    },
    onSuccess: (v, andPost) => {
      toast.success(andPost ? "Journal posted" : "Draft saved");
      qc.invalidateQueries({ queryKey: ["accounting-vouchers", hospitalId] });
      qc.invalidateQueries({ queryKey: ["accounting-dashboard", hospitalId] });
      navigate(`../${v.id}`);
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Failed to save voucher"),
  });

  if (!canPost) {
    return (
      <p className="text-sm text-slate-600">
        You do not have permission to create vouchers.{" "}
        <Link className="underline" to="..">
          Back to list
        </Link>
      </p>
    );
  }

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">New journal voucher</h2>
        <Button asChild variant="ghost" size="sm">
          <Link to="..">Cancel</Link>
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Voucher date</Label>
          <Input
            type="date"
            value={voucherDate}
            onChange={(e) => setVoucherDate(e.target.value)}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label>Narration</Label>
          <Textarea
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            rows={2}
            placeholder="Optional description"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">Account</TableHead>
              <TableHead className="w-32">Debit</TableHead>
              <TableHead className="w-32">Credit</TableHead>
              <TableHead>Line narration</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.key}>
                <TableCell>
                  <Select
                    value={line.ledgerAccountId || undefined}
                    onValueChange={(v) => updateLine(line.key, { ledgerAccountId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {(accounts as LedgerAccount[]).map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Input
                    inputMode="decimal"
                    value={line.debit}
                    onChange={(e) =>
                      updateLine(line.key, {
                        debit: e.target.value,
                        credit: e.target.value ? "" : line.credit,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    inputMode="decimal"
                    value={line.credit}
                    onChange={(e) =>
                      updateLine(line.key, {
                        credit: e.target.value,
                        debit: e.target.value ? "" : line.debit,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={line.narration}
                    onChange={(e) => updateLine(line.key, { narration: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={lines.length <= 2}
                    onClick={() =>
                      setLines((prev) => prev.filter((l) => l.key !== line.key))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setLines((prev) => [...prev, emptyLine()])}
        >
          <Plus className="mr-1 h-4 w-4" /> Add line
        </Button>
        <div className="text-sm">
          <span className="mr-4">Debit {formatInr(totals.debit)}</span>
          <span className="mr-4">Credit {formatInr(totals.credit)}</span>
          <span
            className={
              totals.balanced ? "font-medium text-emerald-700" : "font-medium text-rose-600"
            }
          >
            {totals.balanced ? "Balanced" : "Out of balance"}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!totals.balanced || createMut.isPending}
          variant="outline"
          onClick={() => createMut.mutate(false)}
        >
          Save draft
        </Button>
        <Button
          disabled={!totals.balanced || createMut.isPending}
          onClick={() => createMut.mutate(true)}
        >
          Save & post
        </Button>
      </div>
    </div>
  );
}
